import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import CircularProgress from "@mui/material/CircularProgress";
import { useLazyQuery } from "@apollo/client";
import { GLOBAL_SEARCH } from "graphql/service";
import { Container, StyledInput, StyledList, StyledItem } from "./styles";
import { debounce, set } from "lodash-es";
import { debouncedErrorMessage } from "utils/messageUtil";

const SearchBar = ({ additionalstyle }) => {
  const navigate = useNavigate();
  const latestQueryRef = useRef("");
  const [isActive, setIsActive] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [keyword, setKeyword] = useState("");

  const [
    globalSearch,
    { loading: globalSearchLoading, data: searchData, error },
  ] = useLazyQuery(GLOBAL_SEARCH, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes("Validation")) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  useEffect(() => {
    if (!globalSearchLoading) {
      setIsSearching(false);
    }
  }, [globalSearchLoading]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (!value.trim()) return;

        setIsTyping(false);  
        setIsSearching(true);
        latestQueryRef.current = value;
        
        globalSearch({
          variables: {
            input: {
              query: value,
            },
          },
        });
      }, 200),
    [globalSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const extractSearchableValues = (obj, path = []) => {
    if (obj == null) return [];

    if (typeof obj === "string" || typeof obj === "number") {
      return [{ path, value: String(obj) }];
    }

    if (Array.isArray(obj)) {
      return obj.flatMap((item, index) =>
        extractSearchableValues(item, [...path, index])
      );
    }

    if (typeof obj === "object") {
      return Object.entries(obj).flatMap(([key, value]) =>
        extractSearchableValues(value, [...path, key])
      );
    }

    return [];
  };

  const FIELD_PRIORITY = [
    { key: "referenceNumber", label: "Reference#" },
    { key: "reservationCode", label: "Code" },
    { key: "bookingReservationCode", label: "Code" },
    { key: "roomNumber", label: "Room" },
    { key: "fullName", label: "Name" },
    { key: "holderName", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phoneNumber", label: "Phone" },
    { key: "bookingId", label: "Booking" },
    { key: "groupId", label: "Group" },
  ];

  const buildDisplayInfo = (item) => {
    const matchedFields = item.matchedFields ?? [];

    const priorityMap = new Map(
      FIELD_PRIORITY.map((f, index) => [f.key, index])
    );

    const labelMap = new Map(
      FIELD_PRIORITY.map((f) => [f.key, f.label])
    );

    const primaryMatch = matchedFields
      .slice()
      .sort((a, b) => {
        const aKey = a.path[a.path.length - 1];
        const bKey = b.path[b.path.length - 1];

        const priorityDiff =
          (priorityMap.get(aKey) ?? FIELD_PRIORITY.length) -
          (priorityMap.get(bKey) ?? FIELD_PRIORITY.length);

        if (priorityDiff !== 0) return priorityDiff;

        // shorter value wins if same field
        return a.rawValue.length - b.rawValue.length;
      })[0];

    const primaryKey = primaryMatch?.path.at(-1);

    const data = item.data ?? {};

    const secondaryInfo = FIELD_PRIORITY
      .filter(({ key }) => key !== primaryKey && data[key])
      .map(({ key, label }) => `${label}: ${data[key]}`);

    return {
      primaryMatch,
      primaryLabel: labelMap.get(primaryKey),
      secondaryInfo,
    };
  };

  const options = useMemo(() => {
    const results = searchData?.globalSearch;
    if (!Array.isArray(results) || !keyword || keyword !== latestQueryRef.current) return [];

    const lowerCase = keyword.toLowerCase();

    return results.flatMap((result) =>
      result.items
        .map((item) => {
          const searchableValues = extractSearchableValues(item.data);

          const matchedFields = searchableValues
            .map(({ path, value }) => {
              const matches = match(value, lowerCase, {
                insideWords: true,
                findAllOccurrences: true,
              });

              if (!matches.length) return null;

              return {
                path,
                rawValue: value,
                highlightedValue: parse(value, matches),
              };
            })
            .filter(Boolean);

          if (!matchedFields.length) return null;

          return {
            ...item,
            matchedFields,
            display: buildDisplayInfo({
              ...item,
              matchedFields,
            }),
          };
        })
        .filter(Boolean)
    );
  }, [searchData, keyword]);

  const inputHandler = (value) => {
    setKeyword(value);

    if (!value.trim()) {
      setIsActive(false);
      setIsTyping(false);
      setIsSearching(false);
      debouncedSearch.cancel();
      return;
    }

    setIsActive(true);
    setIsTyping(true);

    debouncedSearch(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      navigate("/search-results", { state: { keyword } });
      setIsActive(false);
      setKeyword("");
    }
  };

  return (
    <Container additionalstyle={additionalstyle}>
      <StyledInput
        placeholder="Search..."
        onBlur={() => {
          setKeyword("");
          setIsActive(false);
        }}
        onKeyDown={handleKeyDown}
        value={keyword}
        onChange={(e) => inputHandler(e.target.value)}
      />

      {(isTyping || isSearching) && (
        <CircularProgress
          style={{ position: "absolute", top: "11px", right: "140px" }}
          color="success"
          thickness={10}
          size={20}
        />
      )}
      {isActive && !globalSearchLoading && options.length > 0 && (
        <StyledList>
          {options.map((item) => {
            const { primaryMatch, primaryLabel, secondaryInfo } = item.display;
            let metaInfo = ""
            secondaryInfo?.forEach((info, index) => (
              metaInfo += `${index > 0 ? " " : ""}${info}`
            ));

            const getHref = (item) => {
              switch (item.type) {
                case "BOOKING":
                case "IDENTITY_SCAN":
                case "PAYMENT_CARD":
                case "TRANSACTION":
                case "TENANT_PARKING":
                case "GROUP":
                  return `/booking-details/${item.data.groupId}`;
                case "USER":
                  return `/tenant-details/${item.data.tenantId}`;
                case "ROOM":
                  return `/room-details/${item.data.roomNumber}`;
                default:
                  return "#";
              }
            };

            return (
              <StyledItem
                key={item.id}
                to={getHref(item)}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={(e) => {
                  setIsActive(false);
                  setKeyword("");
                }}
              >
                <div className="title">
                  <span className="type">{item.type}: </span>
                  <span className="info">
                    <span className="matchedInfo">
                      {primaryLabel && <span>{primaryLabel}: </span>}
                      {primaryMatch?.highlightedValue.map((str, index) => (
                        <span
                          key={index}
                          className={str.highlight ? "highlight" : ""}
                        >
                          {str.text}
                        </span>
                      ))}
                    </span>
                    {secondaryInfo.length > 0 &&
                      <span className="secondaryInfo">
                        {metaInfo}
                      </span>
                    }
                  </span>
                </div>
              </StyledItem>
            );
          })}
        </StyledList>
      )}
    </Container>
  );
};

export default SearchBar;